// Sentry browser instrumentation. MUST be imported BEFORE any React/router
// import so the SDK installs its global handlers ahead of the app boot.
//
// Configuration goals (kept minimal on purpose):
//   • Errors only, no performance tracing (`tracesSampleRate: 0`)
//   • Session replay only when an error fires (`replaysOnErrorSampleRate: 1`)
//   • Filter out noisy network/extension errors so the dashboard stays clean
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || "development",
    // No transactions — we only care about errors right now.
    tracesSampleRate: 0,
    // Replay buffer is captured all the time (locally) but only uploaded
    // when an error fires, giving us the last ~60s of UI before a crash.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Hide common ad-blocker / network-spam noise.
    ignoreErrors: [
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource",
      "TypeError: Load failed",
      "Network request failed",
      "AbortError",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      // Browser extensions throw these and they are not actionable.
      "Non-Error promise rejection captured",
    ],
    beforeSend(event) {
      const msg =
        event.exception?.values?.[0]?.value ||
        event.message ||
        "";
      if (
        msg.includes("blocked by CORS policy") ||
        msg.includes("chrome-extension://") ||
        msg.includes("moz-extension://")
      ) {
        return null;
      }
      return event;
    },
  });
} else if (process.env.NODE_ENV !== "test") {
  // Surface a single warning during dev so the team notices the missing DSN
  // without spamming the console — production builds run silently.
  // eslint-disable-next-line no-console
  console.info("[sentry] REACT_APP_SENTRY_DSN not configured — error tracking disabled");
}

// ────────────────────────────────────────────────────────────────
// Iter177 — Native JS error reporter to our own backend
// (`/api/errors/report`). Complementa o Sentry (roda mesmo sem
// DSN) e alimenta o card admin "Saúde → Erros do frontend".
// Zero deps, ~30 linhas.
//
// Estratégia anti-loop:
//   • Debounce por ``signature`` (msg + primeiro frame): mesmo erro
//     não é reportado 2 vezes em <5s (evita spam quando um render
//     entra em loop de re-throw).
//   • Só reporta em production e em preview.emergentagent.com — em
//     `localhost` (dev) fica silencioso pra não poluir os testes.
// ────────────────────────────────────────────────────────────────
(() => {
  const isBrowser = typeof window !== "undefined";
  if (!isBrowser) return;
  const host = window.location.hostname || "";
  const isProd = /\.(com\.br|com|net)$/i.test(host) || host.includes("emergentagent.com");
  if (!isProd) return;

  const IGNORE_PATTERNS = [
    /ResizeObserver/i,
    /Non-Error promise rejection/i,
    /chrome-extension:/i,
    /moz-extension:/i,
    /Load failed$/,
    /Failed to fetch/i,
    /Network request failed/i,
    /AbortError/i,
  ];
  // Iter189 — padrões adicionais que avaliamos contra a STACK (não a
  // mensagem). O Google Translate joga erros com mensagens genéricas
  // ("Cannot read properties of null", "Maximum call stack size
  // exceeded") mas o stack SEMPRE contém ``translate_http`` ou
  // ``translate.googleapis.com``. Filtramos aí. Também absorvemos os
  // erros de chunk stale — tratados adiante com auto-recovery.
  const IGNORE_STACK_PATTERNS = [
    /translate_http/i,
    /translate\.googleapis\.com/i,
    /translate\.google\.com/i,
    // VLibras (gov.br) — plugin Unity WebGL de LIBRAS. Também mora
    // fora do nosso controle e joga erros de dispose semelhantes.
    /vlibras\.gov\.br/i,
  ];
  const seen = new Map(); // signature -> last-ts

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const report = (message, stack) => {
    if (!message) return;
    if (IGNORE_PATTERNS.some((rx) => rx.test(message))) return;
    // Iter189 — filtro por STACK: absorve erros gerados por scripts
    // de terceiros (Google Translate, VLibras) que carregam scripts
    // externos fora do nosso controle. As mensagens são genéricas
    // ("Cannot read properties of null"), então precisamos verificar
    // o stack para diferenciar de bugs reais nossos.
    if (stack && IGNORE_STACK_PATTERNS.some((rx) => rx.test(stack))) return;
    // Debounce por 5s.
    const sig = (message + (stack || "").split("\n")[0]).slice(0, 240);
    const now = Date.now();
    const last = seen.get(sig) || 0;
    if (now - last < 5000) return;
    seen.set(sig, now);

    try {
      const body = JSON.stringify({
        message: String(message).slice(0, 2000),
        stack: stack ? String(stack).slice(0, 8000) : null,
        url: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent.slice(0, 500),
      });
      const url = `${backend}/api/errors/report`;
      // ``sendBeacon`` sobrevive a navegação/reload; se indisponível, fetch com keepalive.
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
      }
    } catch (_) { /* noop */ }
  };

  window.addEventListener("error", (e) => {
    report(e.message || (e.error && e.error.message), e.error && e.error.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    if (!r) return;
    report(typeof r === "string" ? r : r.message, r.stack);
  });

  // Exposto pro ErrorBoundary do React chamar via ``window.__jt_reportError``.
  window.__jt_reportError = (err, info) => {
    if (!err) return;
    const stack = err.stack || (info && info.componentStack) || null;
    report(err.message || String(err), stack);
  };

  // ─────────────────────────────────────────────────────────────
  // Iter189 — Auto-recovery de ChunkLoadError após redeploy.
  //
  // Quando um usuário está com a aba aberta no momento em que
  // publicamos uma nova build, o webpack no client tenta buscar
  // ``/static/js/XXXX.abcd.chunk.js`` (hash antigo) — o servidor
  // retorna 404 e o import() falha com ``ChunkLoadError``. A UX
  // resultante é uma tela em branco silenciosa. Solução aceita
  // pela indústria (Netflix, Vercel, Airbnb): recarregar a página
  // UMA vez. sessionStorage impede loop caso o chunk realmente
  // não exista (bug de build).
  //
  // Iter211 — expandido para cobrir a família toda de padrões que
  // aparecem em builds modernas (webpack + native ESM):
  //   • ChunkLoadError, Loading chunk N failed (webpack)
  //   • Failed to fetch dynamically imported module (native ESM)
  //   • Importing a module script failed (Safari)
  //   • Error loading dynamically imported module (Vite/Chrome)
  //   • Unable to preload CSS (link[rel=modulepreload])
  // Também exposto como ``window.__jt_isChunkError(err)`` +
  // ``window.__jt_chunkReload()`` pra que o ChunkErrorBoundary do
  // React reaproveite a mesma detecção + guard sem duplicar código.
  // ─────────────────────────────────────────────────────────────
  const CHUNK_PATTERNS = /(?:ChunkLoadError|Loading\s+(?:CSS\s+)?chunk\s+[\w.-]+\s+failed|Failed\s+to\s+fetch\s+dynamically\s+imported\s+module|Importing\s+a\s+module\s+script\s+failed|Error\s+loading\s+dynamically\s+imported\s+module|Unable\s+to\s+preload\s+CSS)/i;

  const isChunkError = (err) => {
    if (!err) return false;
    if (typeof err === "string") return CHUNK_PATTERNS.test(err);
    const msg = err.message || String(err) || "";
    if (CHUNK_PATTERNS.test(msg)) return true;
    // Alguns browsers rotulam err.name === "ChunkLoadError" mas o
    // message vem sem prefixo — checa o name também.
    if (err.name && CHUNK_PATTERNS.test(err.name)) return true;
    return false;
  };

  // Iter241 — Failsafe para redes corporativas com proxy agressivo.
  // Sintoma: usuário atrás de firewall/proxy que cacheia HTML entra em
  // loop infinito de reload — o browser tenta reload, o proxy serve a
  // cópia antiga, o chunk erra de novo, tentamos reload de novo… até o
  // usuário fechar a aba. Solução: contador de tentativas (não boolean)
  // com teto de 2 reloads por sessão. Estourou o teto, para de tentar
  // e deixa o modal ``chunk-fallback`` no index.js decidir o que fazer
  // (mostra dica de Ctrl+F5 + botão pra continuar navegando na versão
  // antiga em vez de bater a cabeça na mesma parede).
  //
  // ``window.__jt_chunkReloadAttempts()`` — exposto pro ChunkErrorBoundary
  // ler o contador atual e ajustar UX quando >= 2.
  const CHUNK_RELOAD_KEY = "jt-chunk-reload";  // valor: número (ou "1" legado)
  const CHUNK_RELOAD_MAX = 2;

  const getChunkReloadAttempts = () => {
    try {
      const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (raw === null || raw === "") return 0;
      // Compat com formato boolean antigo ("1"): trata como 1 tentativa.
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch (_) { return 0; }
  };

  const attemptChunkReload = () => {
    try {
      const attempts = getChunkReloadAttempts();
      if (attempts >= CHUNK_RELOAD_MAX) return; // failsafe: chegou no teto
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1));
      window.location.reload();
    } catch (_) { /* storage bloqueado — só perde o guard, sem loop */ }
  };
  window.__jt_isChunkError = isChunkError;
  window.__jt_chunkReload = attemptChunkReload;
  window.__jt_chunkReloadAttempts = getChunkReloadAttempts;
  window.__jt_chunkReloadMax = CHUNK_RELOAD_MAX;

  window.addEventListener("error", (e) => {
    // Erros disparados por <script> tag (loading chunk) chegam como
    // ErrorEvent com target === HTMLScriptElement e message vazio.
    if (e && e.target && e.target.tagName === "SCRIPT" && /\.chunk\.js/.test(e.target.src || "")) {
      attemptChunkReload();
    }
    // Também <link rel="modulepreload"> e <link rel="stylesheet">
    // podem 404 em builds novas — mesma classe de problema.
    if (e && e.target && e.target.tagName === "LINK" && /\.(js|css)$/.test(e.target.href || "")) {
      const rel = (e.target.rel || "").toLowerCase();
      if (rel === "modulepreload" || rel === "stylesheet") {
        attemptChunkReload();
      }
    }
  }, true); // useCapture — script errors não borbulham
  window.addEventListener("error", (e) => {
    if (isChunkError(e?.error) || CHUNK_PATTERNS.test(e?.message || "")) {
      attemptChunkReload();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError(e?.reason)) {
      attemptChunkReload();
    }
  });
  // Limpa o guard uma vez que o SPA hidrata com sucesso — permite
  // que futuras janelas de redeploy sejam recuperadas também.
  const clearChunkGuard = () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem("jt-chunk-reload");
      } catch (_) {
        /* storage bloqueado — nada a fazer */
      }
    }, 5000);
  };
  if (document.readyState === "complete") {
    clearChunkGuard();
  } else {
    window.addEventListener("load", clearChunkGuard);
  }

  // ─────────────────────────────────────────────────────────────
  // Iter244 → Iter245 — Para-quedas DOM watchdog SILENCIOSO.
  //
  // Se a árvore React desmontar por qualquer motivo (WSOD por bug
  // no ErrorBoundary, chunk catastrófico durante hidratação, etc.),
  // o div#root fica vazio. Este watchdog checa 5.5s após o load
  // — se o root continua sem filhos, dispara um hard reload
  // proxy-proof SEM MOSTRAR NADA ao usuário.
  //
  // Decisão de produto (Iter245): NENHUMA UI visível sobre problemas
  // de cache/versão. Se o reload não resolver, o usuário fica
  // temporariamente sem app — melhor do que uma tela travada com
  // botão. A próxima navegação normal do usuário provavelmente
  // servirá conteúdo fresco.
  //
  // ``__jt_wsod_recovering`` é setado pelo ChunkErrorBoundary
  // quando o mesmo já está agendando um reload — pra não gerar
  // duplicidade nem "corrida" entre os dois timers.
  // ─────────────────────────────────────────────────────────────
  const scheduleWsodWatchdog = () => {
    window.setTimeout(() => {
      try {
        if (window.__jt_wsod_recovering) return; // ChunkErrorBoundary já cuida
        const rootEl = document.getElementById("root");
        if (!rootEl) return;
        // Conteúdo pré-renderizado (#jt-ssr) não conta como React montado.
        const kids = Array.from(rootEl.children).filter((el) => el.id !== "jt-ssr");
        if (kids.length > 0) return; // React renderizou algo
        // Detectou WSOD. Marca a flag pra ninguém disparar de novo.
        window.__jt_wsod_recovering = true;
        // Reporta pro backend antes de reload (se o helper subiu).
        try {
          if (window.__jt_reportError) {
            window.__jt_reportError(
              new Error("WSOD watchdog fired: #root empty 5.5s after load"),
              { componentStack: "wsod-watchdog" },
            );
          }
        } catch (_) { /* noop */ }
        // Reload SILENCIOSO proxy-proof — sem UI intermediária.
        // Se o navegador aceitar location.href, o usuário verá o
        // pisca-pisca padrão de navegação; nenhum modal do JurisTube.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("_v");
          url.searchParams.set("_v", String(Date.now()));
          window.location.href = url.toString();
        } catch (_) {
          try { window.location.reload(); } catch (__) { /* nada mais a tentar */ }
        }
      } catch (_) { /* watchdog não pode crashar — silencioso */ }
    }, 5500);
  };
  if (document.readyState === "complete") {
    scheduleWsodWatchdog();
  } else {
    window.addEventListener("load", scheduleWsodWatchdog);
  }
})();
